import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, Loader2, Smartphone, Monitor, Trash2, ShieldCheck, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// §1.6 Encryption-key devices: every Cipher Lock identity on the account + Forget (drops the device and
// pulls every wrap issued to it). The local device is never forgettable from here.
export function EncryptionDevicesCard() {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["pqc-identity"], queryFn: api.pqcIdentity, retry: false });
  const forget = useMutation({
    mutationFn: (fp) => api.pqcForgetDevice(fp),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["pqc-identity"] });
      toast.success(`Device forgotten${typeof r?.wraps_removed === "number" ? ` · ${r.wraps_removed} key wrap${r.wraps_removed === 1 ? "" : "s"} revoked` : ""}`);
    },
    onError: () => toast.error("Couldn't forget that device."),
    onSettled: () => setConfirm(null),
  });
  const devices = data?.devices || [];
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? devices : devices.filter((d, i) => d.current || i < 5);
  const hidden = devices.length - visible.length;
  return (
    <div id="encryption-devices-card" className="rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="encryption-devices-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <KeyRound className="h-4 w-4 text-bloq-purple" /> Encryption keys · devices
      </div>
      <p className="text-xs text-bloq-muted">Each device holds its own Cipher Lock identity. Forget a device you no longer use to revoke the message keys wrapped for it.</p>
      <div className="mt-4 space-y-2">
        {isLoading ? (
          <div className="flex justify-center py-6 text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : devices.length === 0 ? (
          <div className="rounded-2xl border border-white/8 px-4 py-4 text-center text-xs text-bloq-muted">No Cipher Lock devices yet — open an encrypted chat to publish this one.</div>
        ) : (
          visible.map((d) => {
            const Icon = d.platform === "web" ? Monitor : Smartphone;
            return (
              <div key={d.fp} className={cn("flex items-center gap-3 rounded-2xl border px-4 py-3", d.current ? "border-bloq-mint/30 bg-bloq-mint/5" : "border-white/8")} data-testid={`pqc-device-${d.fp}`}>
                <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", d.current ? "bg-bloq-mint/15 text-bloq-mint" : "bg-white/5 text-bloq-muted")}><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-white">
                    <span className="truncate">{d.label || "Cipher Lock device"}</span>
                    {d.current && <span className="rounded-full border border-bloq-mint/40 bg-bloq-mint/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-bloq-mint">This device</span>}
                    {d.signed ? <ShieldCheck className="h-3.5 w-3.5 text-bloq-mint" title="ML-DSA-65 signed" /> : <ShieldAlert className="h-3.5 w-3.5 text-amber-400" title="Unsigned bundle" />}
                  </div>
                  <div className="truncate font-mono text-[11px] text-bloq-muted">{d.fp} · seen {timeAgo(d.last_seen || d.first_seen)}</div>
                </div>
                {!d.current && (
                  <button onClick={() => setConfirm(d)} disabled={forget.isPending} className="flex h-8 w-8 items-center justify-center rounded-full text-bloq-muted hover:bg-destructive/15 hover:text-destructive disabled:opacity-50" title="Forget this device" data-testid={`pqc-forget-${d.fp}`}>
                    {forget.isPending && forget.variables === d.fp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                )}
              </div>
            );
          })
        )}
        {(hidden > 0 || showAll) && devices.length > 5 && (
          <button onClick={() => setShowAll((s) => !s)} className="w-full rounded-2xl border border-dashed border-white/10 py-2 text-xs font-semibold text-bloq-muted hover:text-white" data-testid="pqc-devices-toggle">
            {showAll ? "Show fewer devices" : `Show ${hidden} more ${hidden === 1 ? "device" : "devices"}`}
          </button>
        )}
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="pqc-forget-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Forget “{confirm?.label || "this device"}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-bloq-muted">
              Its Cipher Lock identity is removed and every message key wrapped for it is revoked. That device can no longer open your encrypted chats until it publishes a new identity.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-white/5" data-testid="pqc-forget-cancel">Keep</AlertDialogCancel>
            <AlertDialogAction onClick={() => forget.mutate(confirm.fp)} className="bg-destructive text-white hover:bg-destructive/90" data-testid="pqc-forget-confirm">Forget device</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
